import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", code: "", active: true };

export default function AdminCustomers() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [search, setSearch] = useState("");

    const load = () => http.get("/customers").then((r) => setItems(r.data));
    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (form.customer_id) {
                await http.put(`/customers/${form.customer_id}`, form);
                toast.success("Cliente actualizado");
            } else {
                await http.post("/customers", form);
                toast.success("Cliente creado");
            }
            setOpen(false);
            setForm(empty);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`¿Desactivar ${c.name}?`)) return;
        await http.delete(`/customers/${c.customer_id}`);
        load();
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                (c.code || "").toLowerCase().includes(q),
        );
    }, [items, search]);

    const accessors = useMemo(
        () => ({
            name: (c) => c.name || "",
            code: (c) => c.code || "",
            active: (c) => (c.active ? 1 : 0),
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        filtered,
        "admin-customers",
        accessors,
        { initialKey: "name", initialDir: "asc" },
    );

    return (
        <div data-testid="page-admin-customers">
            <PageHeader
                eyebrow="Administración"
                title="Clientes"
                description="Catálogo de clientes para imputación de horas."
                actions={
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                onClick={() => setForm(empty)}
                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                data-testid="customer-new-btn"
                            >
                                <Plus size={14} className="mr-2" /> Nuevo cliente
                            </Button>
                        </SheetTrigger>
                        <SheetContent className="rounded-none sm:max-w-md">
                            <SheetHeader>
                                <SheetTitle className="font-display">
                                    {form.customer_id ? "Editar cliente" : "Nuevo cliente"}
                                </SheetTitle>
                            </SheetHeader>
                            <form onSubmit={submit} className="mt-6 space-y-3">
                                <div>
                                    <Label className="text-xs">Nombre</Label>
                                    <Input
                                        required
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({ ...form, name: e.target.value })
                                        }
                                        className="mt-1 rounded-sm border-slate-300"
                                        data-testid="customer-name"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Código</Label>
                                    <Input
                                        value={form.code || ""}
                                        onChange={(e) =>
                                            setForm({ ...form, code: e.target.value })
                                        }
                                        className="mt-1 rounded-sm border-slate-300 font-mono"
                                        data-testid="customer-code"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                    data-testid="customer-save"
                                >
                                    {form.customer_id ? "Guardar" : "Crear"}
                                </Button>
                            </form>
                        </SheetContent>
                    </Sheet>
                }
            />
            <div className="p-6 lg:p-10">
                <div className="flex flex-wrap items-center gap-3 mb-4 border border-slate-200 bg-white p-3">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MagnifyingGlass
                            size={14}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <Input
                            placeholder="Buscar por nombre o código..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="rounded-sm border-slate-300 pl-7 h-8"
                            data-testid="customers-search"
                        />
                    </div>
                    <span className="text-xs text-slate-500 ml-auto font-mono">
                        {sorted.length} de {items.length}
                    </span>
                </div>
                <div className="border border-slate-200 bg-white">
                    <table className="w-full text-sm" data-testid="customers-table">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("name", "py-3 px-4 text-left")}>
                                    Nombre
                                    <SortIndicator active={sortKey === "name"} dir={sortDir} />
                                </th>
                                <th {...headerProps("code", "py-3 px-4 text-left")}>
                                    Código
                                    <SortIndicator active={sortKey === "code"} dir={sortDir} />
                                </th>
                                <th {...headerProps("active", "py-3 px-4 text-left")}>
                                    Estado
                                    <SortIndicator
                                        active={sortKey === "active"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((c) => (
                                <tr
                                    key={c.customer_id}
                                    className="border-b border-slate-100 hover:bg-slate-50"
                                >
                                    <td className="py-3 px-4 font-medium">{c.name}</td>
                                    <td className="py-3 px-4 font-mono text-slate-700">
                                        {c.code || "—"}
                                    </td>
                                    <td className="py-3 px-4">
                                        <span
                                            className={`px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}
                                        >
                                            {c.active ? "Activo" : "Inactivo"}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        <button
                                            onClick={() => {
                                                setForm(c);
                                                setOpen(true);
                                            }}
                                            className="text-slate-500 hover:text-slate-900 mr-3"
                                        >
                                            <PencilSimple size={16} />
                                        </button>
                                        <button
                                            onClick={() => remove(c)}
                                            className="text-slate-400 hover:text-red-600"
                                        >
                                            <Trash size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
